import { useCallback, useEffect, useRef, useState } from 'react';

const CHANNEL_NAME = 'mdvip-idle-sync';
const DEBOUNCE_MS = 500;
// Throttle cross-tab broadcasts: send immediately on first activity, then at most
// once every BROADCAST_THROTTLE_MS. Other tabs tolerate up to this much drift on
// their idle timers, which is negligible vs. the 30-minute timeout.
const BROADCAST_THROTTLE_MS = 5000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'click', 'scroll', 'touchstart'] as const;

type ChannelMessage = { type: 'activity' } | { type: 'extend' };

export interface UseIdleTimeoutOptions {
  idleTimeoutMs: number;
  warningDurationMs: number;
  onLogout: () => void;
  onRefresh: () => Promise<void>;
  isEnabled: boolean;
}

export interface UseIdleTimeoutReturn {
  showWarning: boolean;
  remainingSeconds: number;
  extendSession: () => void;
  triggerLogout: () => void;
}

export function useIdleTimeout({
  idleTimeoutMs,
  warningDurationMs,
  onLogout,
  onRefresh,
  isEnabled,
}: UseIdleTimeoutOptions): UseIdleTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(Math.ceil(warningDurationMs / 1000));

  // Refs for timers, channel, and current prop values — stable across renders
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const remainingRef = useRef(Math.ceil(warningDurationMs / 1000));
  const lastBroadcastRef = useRef(0);

  // Keep prop refs current without triggering effects
  const onLogoutRef = useRef(onLogout);
  const onRefreshRef = useRef(onRefresh);
  const idleTimeoutMsRef = useRef(idleTimeoutMs);
  const warningDurationMsRef = useRef(warningDurationMs);

  onLogoutRef.current = onLogout;
  onRefreshRef.current = onRefresh;
  idleTimeoutMsRef.current = idleTimeoutMs;
  warningDurationMsRef.current = warningDurationMs;

  const stopCountdown = useCallback(() => {
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const stopIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    stopCountdown();
    const initial = Math.ceil(warningDurationMsRef.current / 1000);
    remainingRef.current = initial;
    setRemainingSeconds(initial);

    countdownRef.current = setInterval(() => {
      remainingRef.current -= 1;
      setRemainingSeconds(remainingRef.current);
      if (remainingRef.current <= 0) {
        stopCountdown();
        onLogoutRef.current();
      }
    }, 1000);
  }, [stopCountdown]);

  const startIdleTimer = useCallback(() => {
    stopIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
    }, idleTimeoutMsRef.current);
  }, [stopIdleTimer, startCountdown]);

  const extendSession = useCallback(() => {
    stopCountdown();
    setShowWarning(false);
    startIdleTimer();
    channelRef.current?.postMessage({ type: 'extend' } satisfies ChannelMessage);
    onRefreshRef.current().catch((err: unknown) => {
      console.warn('Session refresh failed:', err);
    });
  }, [stopCountdown, startIdleTimer]);

  const triggerLogout = useCallback(() => {
    // AuthContext.logout() handles cross-tab broadcast via the adapter.
    // No broadcast needed here — calling onLogout() propagates through AuthContext.
    onLogoutRef.current();
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      stopIdleTimer();
      stopCountdown();
      setShowWarning(false);
      return;
    }

    // Cross-tab activity synchronization via BroadcastChannel
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = channel;
      channel.onmessage = ({ data }: MessageEvent<ChannelMessage>) => {
        if (data.type === 'activity') {
          stopCountdown();
          setShowWarning(false);
          startIdleTimer();
        } else if (data.type === 'extend') {
          stopCountdown();
          setShowWarning(false);
          startIdleTimer();
        }
      };
    } catch {
      // BroadcastChannel unavailable (e.g. test environments)
    }

    startIdleTimer();

    const onActivity = () => {
      // Stop immediately — must not be debounced (see single-tab fix).
      stopIdleTimer();
      // Throttle cross-tab broadcast: send immediately on first activity after a quiet
      // period, then suppress until BROADCAST_THROTTLE_MS has elapsed. This avoids
      // flooding other tabs at 60fps during continuous mouse movement while still
      // notifying them within a human-perceptible timeframe.
      const now = Date.now();
      if (now - lastBroadcastRef.current >= BROADCAST_THROTTLE_MS) {
        lastBroadcastRef.current = now;
        channelRef.current?.postMessage({ type: 'activity' } satisfies ChannelMessage);
      }
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        startIdleTimer();
      }, DEBOUNCE_MS);
    };

    for (const evt of ACTIVITY_EVENTS) {
      // capture: true is required for `scroll`, which does not bubble and would
      // otherwise be missed on any overflow container. Using capture for all
      // events is harmless and keeps the registration/removal options consistent.
      document.addEventListener(evt, onActivity, { passive: true, capture: true });
    }

    return () => {
      stopIdleTimer();
      stopCountdown();
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
      lastBroadcastRef.current = 0;
      for (const evt of ACTIVITY_EVENTS) {
        // Must match the capture: true used during addEventListener, otherwise
        // removeEventListener silently fails to deregister the handler.
        document.removeEventListener(evt, onActivity, { capture: true });
      }
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [isEnabled, startIdleTimer, stopIdleTimer, stopCountdown]);

  return { showWarning, remainingSeconds, extendSession, triggerLogout };
}
